import React, { useCallback, useEffect, useState } from "react";
import { Table } from "reactstrap";
import TableStyle from "./../../../../components/table/table.module.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";
import TableActionBtnGroup from "../../../../components/table/all-tables/tableactionbtngroup/TableActionBtnGroup";
import {
  downloadFileFromResponse,
  sanitizeInput,
  showToast
} from "../../../../utils/utils";
import {
  ADMIN_ADD_TOKEN,
  showToastType
} from "../../../../utils/constants/appConstants";
import {
  buttonName,
  checklist,
  checkListTab,
  tooltips
} from "../../../../locales/en";
import PropTypes from "prop-types";
import QuestionEmailSendModel from "../../../../components/modals/QuestionEmailSendModel";
import "../Checklist.scss";
import {
  getReportingAddQuestionFileUrl,
  getReportingDeleteQuestionFileUrl,
  getReportingDownloadQuestionFileUrl,
  getReportingSubmitQuestionsUrl,
  getReportingUpdateQuestionFileUrl,
  getReportingUploadQuestionFileUrl
} from "../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../utils/axiosPrivate";

const SelectQuestions = ({ sendData, userId, refreshData, setLoading }) => {
  const [sendQuestionsData, setSendQuestionsData] = useState([]);
  const [question, setQuestion] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [emailModal, setEmailModal] = useState(false);
  const [questionIds, setQuestionIds] = useState([]);

  const triggerFileUpload = useCallback(
    (questionId) => {
      return async () => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = "application/pdf,image/*";

        input.onchange = async (e) => {
          const files = Array.from(e.target.files);
          if (files.length === 0) return;

          try {
            const formData = new FormData();
            files.forEach((file) => {
              formData.append("files", file);
            });
            formData.append("useIn", true);

            const response = await privateAxios.put(
              getReportingUploadQuestionFileUrl(questionId),
              formData,
              { headers: { "Content-Type": "multipart/form-data" } }
            );

            setSendQuestionsData((prevData) =>
              prevData?.map((item) =>
                item.id === questionId
                  ? { ...item, attachedQuestionFiles: response?.data?.data }
                  : item
              )
            );
            showToast(response?.data?.message, showToastType.success);
          } catch (error) {
            showToast(error?.message, showToastType.error);
          }
        };
        input.click();
      };
    },
    [privateAxios]
  );

  const handleDeleteClick = async (questionId, filePath) => {
    try {
      const response = await privateAxios.delete(
        getReportingDeleteQuestionFileUrl,
        {
          params: {
            questionId,
            filePath,
            useIn: true
          }
        }
      );

      setSendQuestionsData((prevData) =>
        prevData?.map((item) =>
          item.id === questionId
            ? {
                ...item,
                attachedQuestionFiles: item.attachedQuestionFiles?.filter(
                  (file) => file !== filePath
                )
              }
            : item
        )
      );
      showToast(response?.data?.message, showToastType.success);
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType.error);
    }
  };

  const handleDownloadClick = async (filePath) => {
    try {
      const response = await privateAxios.get(
        getReportingDownloadQuestionFileUrl,
        {
          params: { filePath },
          responseType: "blob"
        }
      );

      downloadFileFromResponse(response);

      showToast(checklist?.errors?.fileDownload, showToastType.success);
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType.error);
    }
  };

  const handleAddQuestion = async () => {
    if (!question.trim()) {
      showToast(checklist.errors.addQuestion, showToastType.error);
      return;
    }
    try {
      const response = await privateAxios.post(getReportingAddQuestionFileUrl, {
        userAccountId: userId,
        responseType: "Note",
        checklistType: "Sales",
        questionText: sanitizeInput(question)
      });
      setQuestion("");
      refreshData();
      showToast(response?.data?.message, showToastType.success);
    } catch (error) {
      showToast(error?.message, showToastType.error);
    }
  };

  const handleAllQuestion = (questionId, checked) => {
    if (checked) {
      setQuestionIds((prev) => [...prev, questionId]);
    } else {
      setQuestionIds((prev) => prev.filter((id) => id !== questionId));
    }
  };

  const handleUpdateQuestion = useCallback(
    async (questionId, originalText = "", editedTextValue = "") => {
      const trimmed = editedTextValue.trim();
      const originalTrimmed = originalText.trim();

      if (!trimmed) {
        showToast(checklist.errors.addQuestion, showToastType.error);
        return;
      }

      if (trimmed === originalTrimmed) {
        setEditingId(null);
        return;
      }

      try {
        const payload = {
          id: questionId,
          questionText: trimmed
        };
        const response = await privateAxios.put(
          getReportingUpdateQuestionFileUrl,
          payload
        );

        setSendQuestionsData((prev) => {
          const index = prev?.findIndex((q) => q.id === questionId);
          if (index === -1) return prev;

          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            questionText: trimmed
          };

          return updated;
        });

        showToast(response?.data?.message, showToastType.success);
      } catch (error) {
        showToast(
          error?.response?.data?.message ||
            checklist?.errors?.failedAddQuestion,
          showToastType.error
        );
      } finally {
        setEditingId(null);
      }
    },
    [checklist.errors.addQuestion]
  );

  useEffect(() => {
    if (Array.isArray(sendData)) {
      const sortedData = [...sendData]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .reverse();
      setSendQuestionsData(sortedData);
    } else {
      setSendQuestionsData([]);
    }
  }, [sendData]);

  const handleEmailModalChange = () => {
    if (questionIds.length === 0) {
      showToast(checklist.errors.selectQuestion, showToastType.error);
      return;
    }
    setEmailModal((prev) => !prev);
  };

  const handleAdminQuestionAdd = async () => {
    if (questionIds.length === 0) {
      showToast(checklist.errors.selectQuestion, showToastType.error);
      return;
    }
    try {
      const response = await privateAxios.post(getReportingSubmitQuestionsUrl, {
        userAccountId: userId,
        questionIds: questionIds,
        token: ADMIN_ADD_TOKEN
      });
      refreshData();
      showToast(response?.data?.message, showToastType.success);
    } catch (error) {
      showToast(error?.message, showToastType.error);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <Table
            className={`${TableStyle.commonCustomtable} checklist-table no-border mb-5`}
          >
            <thead>
              <tr>
                <th>{checkListTab.selectQuestion.th1}</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan="3">
                  <div className="mt-4 mb-4">
                    <textarea
                      className="form-control mb-3"
                      rows={2}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder={checkListTab.selectQuestion.placeholder}
                    />
                    <button
                      type="button"
                      className="btn btn-primary rounded"
                      onClick={handleAddQuestion}
                    >
                      {checkListTab.selectQuestion.addBtn}
                    </button>
                  </div>
                </td>
              </tr>

              {sendQuestionsData?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="checklist-align-wrapper">
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleAllQuestion(item.id, e.target.checked)
                        }
                      />

                      <div className="flex-grow-1 pl-2">
                        {item.questionLabel && (
                          <strong className="label-text ">
                            {item.questionLabel}:
                          </strong>
                        )}

                        {editingId === item.id ? (
                          <textarea
                            className="form-control mt-2"
                            rows={2}
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            onBlur={() =>
                              handleUpdateQuestion(
                                item.id,
                                item.questionText || "",
                                editedText
                              )
                            }
                            autoFocus
                          />
                        ) : (
                          <button
                            type="button"
                            className="editable-text"
                            onClick={() => {
                              setEditingId(item.id);
                              setEditedText(item.questionText);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setEditingId(item.id);
                                setEditedText(item.questionText);
                              }
                            }}
                          >
                            {item.questionText}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column gap-1">
                      {item.attachedQuestionFiles?.map((filePath, index) => (
                        <div
                          key={filePath}
                          className="d-flex align-items-center justify-content-between text-truncate"
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 text-primary text-decoration-underline text-start w-100"
                            onClick={() => handleDownloadClick(filePath)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleDownloadClick(filePath);
                              }
                            }}
                          >
                            {filePath
                              .replace(/^questions\/\d+\//, "")
                              .replace(/(.*)(_\d+_\d+)(\.\w+)$/, "$1$3")}
                          </button>
                          <TableActionBtnGroup
                            onDelete={() =>
                              handleDeleteClick(item.id, filePath)
                            }
                            tooltips={{
                              onDelete: {
                                id: `tooltip-delete-${item.id}-${index}`,
                                content: tooltips.delete,
                                place: "top"
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </td>

                  <td>
                    <TableActionBtnGroup
                      upload={triggerFileUpload(item.id)}
                      tooltips={{
                        upload: {
                          id: `tooltip-upload-${item.id}`,
                          content: tooltips.uploadFile,
                          place: "left"
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary rounded"
              onClick={handleEmailModalChange}
            >
              {buttonName?.sendQuestion}
            </button>
            <button
              type="button"
              className="btn btn-primary rounded"
              onClick={handleAdminQuestionAdd}
            >
              {buttonName?.wrireAnswer}
            </button>
          </div>
        </div>
      </div>

      <ReactTooltip className="tooltipcustom" place="top" effect="solid" />

      <QuestionEmailSendModel
        isOpen={emailModal}
        toggle={handleEmailModalChange}
        questionIds={questionIds}
        userId={userId}
        refreshData={refreshData}
        setLoading={setLoading}
      />
    </>
  );
};

SelectQuestions.propTypes = {
  sendData: PropTypes.array,
  userId: PropTypes.string,
  refreshData: PropTypes.func,
  setLoading: PropTypes.func
};

export default SelectQuestions;
